import { createContext } from "react";
import { listedProducts, productState } from "./product-access";
import type { OrganizationContext } from "./types";

export interface ExperienceNavigationItem {
  path: string;
  label: string;
  mobileLabel: string;
  icon: string;
}

/** Presentation only. Product Access and product-native authorization remain authoritative. */
export interface ExperienceDefinition {
  key: string;
  displayName: string;
  mode: "standalone-product" | "vertical-solution";
  routeBase: string;
  homePath: string;
  /** Product-content style scope only; never grants shell/chrome ownership. */
  surfaceClassName?: string;
  primaryNavigation: readonly ExperienceNavigationItem[];
}

export const reviewBoostExperience: ExperienceDefinition = {
  key: "review-boost",
  displayName: "Review Boost",
  mode: "standalone-product",
  routeBase: "reputation",
  homePath: "overview",
  primaryNavigation: [
    { path: "overview", label: "Inicio", mobileLabel: "Inicio", icon: "home" },
    { path: "reviews", label: "Reseñas", mobileLabel: "Reseñas", icon: "reputation" },
    { path: "ai-agent", label: "Agente IA", mobileLabel: "Agente", icon: "agent" },
    { path: "reports", label: "Reportes", mobileLabel: "Reportes", icon: "reports" },
    { path: "settings", label: "Configuración", mobileLabel: "Más", icon: "settings" },
  ],
};

export const salonsExperience: ExperienceDefinition = {
  key: "salons",
  displayName: "Peluquerías",
  mode: "vertical-solution",
  routeBase: "salons",
  homePath: "home",
  surfaceClassName: "cd-salons-shell",
  primaryNavigation: [
    { path: "home", label: "Inicio", mobileLabel: "Inicio", icon: "home" },
    { path: "conversations", label: "Conversaciones", mobileLabel: "Chats", icon: "conversations" },
    { path: "agenda", label: "Agenda", mobileLabel: "Agenda", icon: "calendar" },
    { path: "customers", label: "Clientes", mobileLabel: "Clientes", icon: "customers" },
    { path: "sales", label: "Ventas", mobileLabel: "Ventas", icon: "sales" },
    { path: "reports", label: "Reportes", mobileLabel: "Reportes", icon: "reports" },
    { path: "settings", label: "Administración", mobileLabel: "Admin", icon: "settings" },
  ],
};

const definitions: readonly ExperienceDefinition[] = Object.freeze([
  reviewBoostExperience,
  salonsExperience,
]);

export const ExperienceContext = createContext<ExperienceDefinition | null>(null);

export function experienceDefinitionForRouteBase(routeBase: string): ExperienceDefinition | undefined {
  return definitions.find((definition) => definition.routeBase === routeBase);
}

export function experienceEntryPath(
  organization: OrganizationContext,
  experience: ExperienceDefinition,
): string {
  return `/o/${organization.organization.slug}/${experience.routeBase}/${experience.homePath}`;
}

export function experienceFor(
  organization: OrganizationContext | undefined,
  pathname: string,
): ExperienceDefinition | null {
  if (
    !organization
    || organization.organization.status !== "active"
    || organization.membership.status !== "active"
  ) return null;

  return definitions.find((definition) => {
    const base = `/o/${organization.organization.slug}/${definition.routeBase}`;
    const product = listedProducts(organization.products).find(
      ({ manifest }) => manifest.routeBase === definition.routeBase,
    );
    return (pathname === base || pathname.startsWith(`${base}/`))
      && productState(product?.manifest, product, true) === "available";
  }) ?? null;
}

export function organizationExperienceHome(organization: OrganizationContext): string {
  return `/o/${organization.organization.slug}/home`;
}